import FakeCheckbox from '@/components/FakeCheckbox'
import Icon from '@/components/Icons'
import {
  PricingButtons,
  CheckboxFilters,
  GroupHeader,
  GroupHeaderToggle,
  GroupSublinks,
} from '@/components/widgets/FiltersListingPreview.styles'
import { PRICING_FILTERS } from '@/constant/plp'
import { FiltersListingPreviewEntityType, WidgetCategoryLink, WidgetRegionLink } from '@/types'
import { BREAKPOINT_SMALL, Button } from '@big-red-group/storefront-common-ui'
import React from 'react'
import { scrollToElement } from '@/helpers/PageScrollHelper'
import { pull, uniq } from 'lodash'

const isMobile = () => typeof window !== 'undefined' && window.innerWidth < BREAKPOINT_SMALL
export const scrollToTopOfResults = () => {
  if (isMobile()) {
    setTimeout(() => {
      scrollToElement('productsHeadingWrapper', 50)
    }, 100)
  }
}

export const filterHeading = (entityType: FiltersListingPreviewEntityType, name?: string) => {
  if (name && name !== 'categories') return name

  switch (entityType) {
    case 'THINGS_TO_DO':
      return 'Subcategories'
    case 'CATEGORY':
      return 'Subcategories'
    default:
      return name
  }
}

export const renderCategoryFilters = (
  categories: WidgetCategoryLink[],
  toggledCatGroups: number[],
  setToggledCatGroups: (groups: number[]) => void,
  selectedCatFilters: string[],
  setSelectedCatFilters: (categoryFilters: string[]) => void
) => {
  const toggleGroup = (catId: number) => {
    updateState({ currentState: toggledCatGroups, value: catId, setNewState: setToggledCatGroups })
  }

  const updateCatSelection = (catUrl: string) => {
    updateState({ currentState: selectedCatFilters, value: catUrl, setNewState: setSelectedCatFilters })
  }

  return (
    <CheckboxFilters>
      {categories.map((category, catIndex) => {
        return (
          <React.Fragment key={catIndex}>
            <GroupHeader>
              <FakeCheckbox
                label={category.name}
                checked={selectedCatFilters.includes(category.link)}
                onClick={() => updateCatSelection(category.link)}
              />
              {category.subcategories && (
                <GroupHeaderToggle onClick={() => toggleGroup(catIndex)} active={toggledCatGroups.includes(catIndex)}>
                  <Icon name="caretDown" />
                </GroupHeaderToggle>
              )}
            </GroupHeader>
            {category.subcategories && toggledCatGroups.includes(catIndex) && (
              <GroupSublinks>
                {category.subcategories?.map((subcategory, subcatIndex) => (
                  <FakeCheckbox
                    key={subcatIndex}
                    label={subcategory.name}
                    checked={selectedCatFilters.includes(subcategory.link)}
                    onClick={() => updateCatSelection(subcategory.link)}
                  />
                ))}
              </GroupSublinks>
            )}
          </React.Fragment>
        )
      })}
    </CheckboxFilters>
  )
}

export const renderRegionFilters = (
  regions: WidgetRegionLink[],
  toggledRegionGroups: number[],
  setToggledRegionGroups: (groups: number[]) => void,
  selectedRegionFilters: string[],
  setSelectedRegionFilters: (regionFilters: string[]) => void
) => {
  const toggleGroup = (catId: number) => {
    updateState({ currentState: toggledRegionGroups, value: catId, setNewState: setToggledRegionGroups })
  }

  const updateRegionSelection = (regionUrl: string, subRegionUrls?: string[]) => {
    const items = updateState({
      currentState: selectedRegionFilters,
      value: regionUrl,
      setNewState: setSelectedRegionFilters,
    })
    if (subRegionUrls) {
      const checked = items.indexOf(regionUrl) >= 0
      if (checked) {
        setSelectedRegionFilters(uniq((items as string[]).concat(subRegionUrls)))
      } else {
        setSelectedRegionFilters(pull(items as string[], ...subRegionUrls))
      }
    }
  }

  return (
    <CheckboxFilters>
      {regions.map((region, regionIndex) => {
        return (
          <React.Fragment key={regionIndex}>
            <GroupHeader>
              <FakeCheckbox
                label={region.name}
                checked={selectedRegionFilters.includes(region.link)}
                onClick={() =>
                  updateRegionSelection(
                    region.link,
                    region.regions?.map((r) => r.link)
                  )
                }
              />
              {region.regions && regions.length > 1 && (
                <GroupHeaderToggle
                  onClick={() => toggleGroup(regionIndex)}
                  active={toggledRegionGroups.includes(regionIndex)}
                >
                  <Icon name="caretDown" />
                </GroupHeaderToggle>
              )}
            </GroupHeader>
            {region.regions && (toggledRegionGroups.includes(regionIndex) || regions.length === 1) && (
              <GroupSublinks>
                {region.regions?.map((subregion, subregionIndex) => (
                  <FakeCheckbox
                    key={subregionIndex}
                    label={subregion.name}
                    checked={selectedRegionFilters.includes(subregion.link)}
                    onClick={() => updateRegionSelection(subregion.link)}
                  />
                ))}
              </GroupSublinks>
            )}
          </React.Fragment>
        )
      })}
    </CheckboxFilters>
  )
}

export const renderPriceFilters = (
  pricingMax: number,
  priceRange: number[],
  setPriceRange: (newPriceRange: number[]) => void
) => {
  const availablePrices = PRICING_FILTERS.filter(({ min }) => pricingMax >= min)

  const isActive = (min: number, max: number) => {
    if (priceRange[0] === min && priceRange[1] === max) {
      return true
    }
    return false
  }

  return (
    <PricingButtons>
      {availablePrices.map(({ min, max, label }) => (
        <Button
          size="XS"
          key={label}
          inverse={!isActive(min, max)}
          alternate={!isActive(min, max)}
          onClick={() => {
            setPriceRange([min, max])
            scrollToTopOfResults()
          }}
        >
          {label}
        </Button>
      ))}
    </PricingButtons>
  )
}

type StateProps = {
  currentState: (number | string)[]
  value: number | string
  setNewState: (newState: any[]) => void
  shouldScrollToTop?: boolean
}

function updateState({ currentState, value, setNewState, shouldScrollToTop = true }: StateProps) {
  if (currentState.includes(value)) {
    currentState.splice(currentState.indexOf(value), 1)
  } else {
    currentState.push(value)
  }
  const newState = [...currentState]
  setNewState(newState)
  if (shouldScrollToTop) {
    scrollToTopOfResults()
  }
  return newState
}
